import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect, useRef } from 'react';
import type { RichTextValue } from './rich-text-input.tsx';

// Syncs external `value` prop changes into the Lexical editor. LexicalComposer only
// reads `initialConfig.editorState` on mount, so without this the input is effectively
// uncontrolled after the first render. The stringify check skips the sync when the
// editor already holds the incoming value — which happens right after our own
// OnChangePlugin emits a change up to the parent, avoiding a set/emit loop.
export function ValueSyncPlugin({ value }: { value: RichTextValue | null | undefined }): null {
    const [editor] = useLexicalComposerContext();
    const initialValueRef = useRef(value);

    useEffect(() => {
        if (value === undefined || value === null) {
            return;
        }

        // Skip syncing the initial value: the composer already applied it on mount,
        // and re-applying it will dirty the parent form.
        if (value === initialValueRef.current) {
            return;
        }

        const current = editor.getEditorState().toJSON();
        if (JSON.stringify(current) === JSON.stringify(value)) {
            return;
        }

        editor.setEditorState(editor.parseEditorState(value));
    }, [editor, value]);

    return null;
}
